import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import Reveal from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import { T, useContent } from "@/content/ContentContext";

const IMG = "https://images.pexels.com/photos/4173250/pexels-photo-4173250.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CANDIDATE_STEPS = [
  { n: "01", k: "recruitment.step.1" },
  { n: "02", k: "recruitment.step.2" },
  { n: "03", k: "recruitment.step.3" },
];

export default function RecruitmentPage() {
  const { t, list } = useContent();
  const roles = list("recruitment.roles.items");
  return (
    <div data-testid="recruitment-page">
      <PageHero
        overline={t("recruitment.hero.overline")}
        lines={[<T key="1" k="recruitment.hero.line1" />, <T key="2" k="recruitment.hero.line2" em="text-brand-gold" />]}
        intro={t("recruitment.hero.intro")}
        image={IMG}
        imageAlt="Doctor speaking with a patient"
      />

      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-brand-ink mt-4" data-testid="candidates-heading">{t("recruitment.candidates.heading")}</h2>
              <p className="mt-6 text-base font-light leading-relaxed text-brand-ink/70">
                {t("recruitment.candidates.body")}
              </p>
              <Link to="/contact" data-testid="candidates-cta" className="mt-8 inline-flex items-center gap-3 text-sm font-semibold text-brand-green border-b border-brand-gold pb-1 transition-colors duration-300 hover:text-brand-gold">
                {t("recruitment.candidates.cta")} <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8 border-t border-brand-ink/10">
            {CANDIDATE_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-6 py-8 border-b border-brand-ink/10 px-2" data-testid={`candidate-step-${s.n}`}>
                  <span className="col-span-2 sm:col-span-1 font-sans text-sm font-bold text-brand-gold tracking-[0.2em] pt-1">{s.n}</span>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="font-serif text-xl lg:text-2xl font-medium text-brand-ink">{t(`${s.k}.title`)}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-brand-ink/65 max-w-2xl">{t(`${s.k}.body`)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep text-brand-bone py-24 lg:py-32 texture-grain">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mt-4" data-testid="employers-heading">{t("recruitment.employers.heading")}</h2>
              <p className="mt-6 text-base font-light leading-relaxed text-brand-bone/65">
                {t("recruitment.employers.body")}
              </p>
              <Link to="/contact" data-testid="employers-cta" className="mt-8 inline-flex items-center gap-3 rounded-full bg-brand-gold px-8 py-4 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight hover:-translate-y-0.5">
                {t("recruitment.employers.cta")} <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-bone/10 border border-brand-bone/10">
              {roles.map((r, i) => (
                <Reveal key={r} delay={i * 0.05} className="bg-brand-deep px-6 py-6">
                  <span className="font-serif text-lg italic text-brand-bone/85">{r}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
